import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContractList = ({ contracts, onQuickAction, selectedContracts, onSelectContract }) => {
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'expired':
        return 'bg-error/10 text-error border-error/20';
      case 'draft':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'expired':
        return 'AlertCircle';
      case 'draft':
        return 'Edit3';
      default:
        return 'Circle';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (contracts?.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="FileText" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No contracts found</h3>
        <p className="text-muted-foreground mb-6">
          Try adjusting your filters or create a new contract to get started.
        </p>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
        >
          Create New Contract
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-legal overflow-hidden">
      {/* Table Header */}
      <div className="bg-muted/30 border-b border-border px-6 py-4">
        <div className="grid grid-cols-12 gap-4 items-center text-sm font-medium text-muted-foreground">
          <div className="col-span-1">
            <input
              type="checkbox"
              checked={selectedContracts?.length === contracts?.length && contracts?.length > 0}
              onChange={(e) => {
                if (e?.target?.checked) {
                  contracts?.forEach(contract => onSelectContract(contract?.id, true));
                } else {
                  contracts?.forEach(contract => onSelectContract(contract?.id, false));
                }
              }}
              className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
            />
          </div>
          <div className="col-span-3">Contract</div>
          <div className="col-span-2">Client</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2">Attorney</div>
          <div className="col-span-2">Expiration</div>
          <div className="col-span-1">Actions</div>
        </div>
      </div>
      {/* Table Body */}
      <div className="divide-y divide-border">
        {contracts?.map((contract) => (
          <div
            key={contract?.id}
            className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-muted/20 transition-legal-fast"
          >
            {/* Selection */}
            <div className="col-span-1">
              <input
                type="checkbox"
                checked={selectedContracts?.includes(contract?.id)}
                onChange={(e) => onSelectContract(contract?.id, e?.target?.checked)}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
              />
            </div>

            {/* Contract Info */}
            <div className="col-span-3">
              <Link 
                to="/contract-details" 
                className="block hover:text-primary transition-legal-fast"
              >
                <h4 className="font-medium text-foreground truncate">{contract?.title}</h4>
                <p className="text-sm text-muted-foreground font-mono">{contract?.contractId}</p>
                <p className="text-xs text-muted-foreground">{contract?.type}</p>
              </Link>
            </div>

            {/* Client */}
            <div className="col-span-2">
              <div className="flex items-center space-x-2">
                <Icon name="Building" size={14} className="text-muted-foreground" />
                <span className="text-sm text-foreground truncate">{contract?.client}</span>
              </div>
            </div>

            {/* Status */}
            <div className="col-span-1">
              <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ${getStatusColor(contract?.status)}`}>
                <Icon name={getStatusIcon(contract?.status)} size={12} />
                <span>{contract?.status}</span>
              </div>
            </div>

            {/* Attorney */}
            <div className="col-span-2">
              <div className="flex items-center space-x-2">
                <Icon name="User" size={14} className="text-muted-foreground" />
                <span className="text-sm text-foreground truncate">{contract?.assignedAttorney}</span>
              </div>
            </div>

            {/* Expiration */}
            <div className="col-span-2">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={14} className="text-muted-foreground" />
                <div>
                  <p className="text-sm text-foreground">{formatDate(contract?.expirationDate)}</p>
                  <p className="text-xs text-muted-foreground">{contract?.value}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="col-span-1">
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => onQuickAction('edit', contract)}
                  title="Edit Contract"
                >
                  <Icon name="Edit3" size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => onQuickAction('more', contract)}
                  title="More Actions"
                >
                  <Icon name="MoreHorizontal" size={14} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContractList;